import { useCallback, useEffect, useRef, useState } from 'react';
import { BlockStack } from '@shopify/polaris';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { zebraPrintService, ZebraPrintError } from '#client/services/zebraPrintService';
import { buildProductLabelsPdfUrl } from '#client/components/features/LabelPrinting/utils/productLabels';
import { initialLabelCounts, isLocked, isPrintable, MIN_SEARCH_LENGTH, printableTotals, shouldAutoSelect } from '#client/components/features/LabelPrinting/utils/styleSearch';
import { useStyleSuggest } from '../hooks/useStyleSuggest.js';
import { useStyleResolve } from '../hooks/useStyleResolve.js';
import StyleSearchField from './StyleSearchField.jsx';
import MasterProductCard from './MasterProductCard.jsx';
import StyleVariantTable from './StyleVariantTable.jsx';
import PrintSettingsCard from './PrintSettingsCard.jsx';

export default function ByStyleTab({
    locationGid,
    printMode,
    onPrintModeChange,
    skuType,
    onSkuTypeChange,
    priceSelection,
    onPriceSelectionChange,
    priceListId,
    catalogs,
    onCatalogsChange,
    zebraSelection,
    labelPrintersQuery,
    enableZebraCloudPrinting,
}) {
    const { addToast } = useToast();
    const [searchType, setSearchType] = useState('childSku');
    const [term, setTerm] = useState('');
    const [selected, setSelected] = useState(null);
    // Kept separate from `selected` because the resolve response replaces `selected`
    // outright and carries no `matchedBy` field of its own — folding it into `selected`
    // meant a successful resolve silently dropped the term that got the operator here.
    const [matchedBy, setMatchedBy] = useState(null);
    const [counts, setCounts] = useState({});
    const [isPrinting, setIsPrinting] = useState(false);
    // Set when a resolve fails. `selected` is cleared alongside it so the suggestion list
    // reappears instead of leaving the operator on a blank page (see `handleSelect` and
    // the `resolveError` check below), but the auto-select effect must not treat that
    // clearing as "term is unselected, try again" — otherwise it re-issues the same
    // resolve immediately against an already-failing server, once per render, forever.
    // This flag is the thing that breaks that loop: it is only cleared by an action that
    // means the operator actually moved on (new term, new search type, new store) or by
    // explicitly retrying a match.
    const [resolveError, setResolveError] = useState(null);

    // Bumped by every action that moves the operator on — a new selection, a store
    // change, editing the term, or changing the search type. `onSuccess`/`onError`
    // below drop any response whose token no longer matches the latest one, because by
    // definition the operator has abandoned whatever produced it. This generalizes
    // "drop a superseded response" beyond location: a location match alone let a resolve
    // for a master the operator had already typed past silently resurrect it.
    const latestRequestTokenRef = useRef(0);

    // Which location the *last issued* request was for, so the store-change effect
    // below fires exactly once per change instead of looping against its own update.
    // This is purely a "should we re-fetch" gate; staleness of the response itself is
    // the token's job, not this ref's.
    const lastRequestedLocationRef = useRef(null);

    const suggest = useStyleSuggest({ term, searchType });

    const resolve = useStyleResolve({
        onSuccess: (data, variables) => {
            if (variables.token !== latestRequestTokenRef.current) return;

            setResolveError(null);
            setSelected(data);
            onCatalogsChange(data.catalogs);
            setCounts(initialLabelCounts({ variants: data.variants, inventoryKnown: data.inventoryStatus === 'ok' }));
        },
        onError: (error, variables) => {
            if (variables.token !== latestRequestTokenRef.current) return;

            // Clearing `selected` here (rather than leaving the `{ pending: true }` stub
            // in place) is what brings the suggestion list back instead of leaving the
            // operator on a blank page — `showResults` below only renders while
            // `selected` is falsy. `resolveError` is what stops that same clearing from
            // triggering an auto-select retry storm.
            setSelected(null);
            setResolveError(error.message || 'Failed to load style');
            addToast({ type: 'error', message: error.message || 'Failed to load style' });
        },
    });

    const resolveStyle = resolve.mutate;

    const issueResolve = useCallback(
        (productId, forLocationGid) => {
            const token = ++latestRequestTokenRef.current;
            lastRequestedLocationRef.current = forLocationGid;
            setResolveError(null);
            resolveStyle({ productId, locationGid: forLocationGid, token });
        },
        [resolveStyle]
    );

    const handleSelect = useCallback(
        (match) => {
            setMatchedBy(match.matchedBy);
            setSelected({ pending: true });
            issueResolve(match.productId, locationGid);
        },
        [issueResolve, locationGid]
    );

    // One match loads straight through; two or more wait for the operator to pick.
    //
    // The `term !== suggest.debouncedTerm` guard matters on its own, separately from the
    // token above: clearing `selected` to keep typing re-renders immediately, but the
    // debounce inside useStyleSuggest lags the *raw* term by up to 300ms — so for that
    // window, `suggest.matches`/`debouncedTerm` still describe the term as it was a
    // moment ago, not what is currently typed. Without this check, that stale match set
    // can pass `shouldAutoSelect` and re-select the very master the operator just typed
    // past, immediately and independently of whether any resolve is in flight.
    useEffect(() => {
        if (selected || suggest.isLoading || resolveError) return;
        if (term.trim() !== suggest.debouncedTerm) return;
        if (!shouldAutoSelect({ matches: suggest.matches, term: suggest.debouncedTerm, searchType })) return;

        // `shouldAutoSelect` only guarantees exactly one match has `exactMatch: true`
        // somewhere in the set — substring neighbours can accompany it in any order the
        // server chooses to return them in. Selecting by position instead of by this
        // flag would silently couple this effect to the server's current sort.
        handleSelect(suggest.matches.find((match) => match.exactMatch));
    }, [handleSelect, resolveError, searchType, selected, term, suggest.debouncedTerm, suggest.isLoading, suggest.matches]);

    // A store change after a master is already loaded must re-resolve at the new
    // location: on-hand, catalogs and label counts are all specific to a store, so
    // leaving them as-is would tell the operator one store's numbers under another
    // store's name. The chosen style survives the change; only its numbers refresh.
    useEffect(() => {
        const productId = selected?.masterProduct?.id;
        if (!productId) return;
        if (locationGid === lastRequestedLocationRef.current) return;

        issueResolve(productId, locationGid);
    }, [locationGid, issueResolve, selected]);

    // A store change is also how an operator moves past a resolve failure that left no
    // master selected (so the effect above has no `productId` to re-resolve): clearing
    // the flag here lets auto-select try again at the new store instead of staying
    // suppressed for a location the operator no longer has selected.
    useEffect(() => {
        setResolveError(null);
    }, [locationGid]);

    // Editing the term drops the selection and returns to the list. It must also
    // invalidate any resolve still in flight for the master just abandoned — otherwise
    // that response can land after the clear and silently resurrect the very selection
    // this handler just cleared, badge and all.
    const handleTermChange = useCallback((value) => {
        setTerm(value);
        setSelected(null);
        setMatchedBy(null);
        setCounts({});
        setResolveError(null);
        latestRequestTokenRef.current += 1;
    }, []);

    const handleSearchTypeChange = useCallback((value) => {
        setSearchType(value);
        setSelected(null);
        setMatchedBy(null);
        setCounts({});
        setResolveError(null);
        latestRequestTokenRef.current += 1;
    }, []);

    const handleCountChange = useCallback((sku, value) => {
        if (value !== '' && !/^\d+$/.test(value)) return;
        setCounts((prev) => ({ ...prev, [sku]: value === '' ? 0 : parseInt(value, 10) }));
    }, []);

    const inventoryKnown = selected?.inventoryStatus === 'ok';

    const printableSkus = (selected?.variants ?? [])
        .filter((variant) => isPrintable(variant, skuType) && !isLocked(variant, { inventoryKnown }) && (Number(counts[variant.sku]) || 0) > 0)
        .map((variant) => variant.sku);

    const handlePrint = useCallback(async () => {
        if (printableSkus.length === 0) return;

        if (!priceSelection) {
            addToast({ type: 'error', message: 'Please choose a price catalog' });
            return;
        }

        const totals = printableTotals({ variants: selected.variants, counts, skuType, inventoryKnown });

        if (printMode === 'pdf') {
            window.open(buildProductLabelsPdfUrl({ skus: printableSkus, quantitiesBySku: counts, skuType, priceListId }), '_blank');
            addToast({ type: 'success', message: `Successfully opened ${totals.labels} labels` });
            return;
        }

        const selectionError = zebraSelection.getSelectionError();
        if (selectionError) {
            addToast({ type: 'error', message: selectionError });
            return;
        }

        try {
            setIsPrinting(true);
            await zebraPrintService.printLabels(
                printableSkus.map((sku) => ({ sku, quantity: counts[sku] })),
                zebraSelection.selectedPrinterDeviceId,
                { skuType, priceListId }
            );
            addToast({ type: 'success', message: `Successfully sent ${totals.labels} labels to printer` });
        } catch (error) {
            addToast({ type: 'error', message: error instanceof ZebraPrintError ? error.getUserFriendlyMessage() : `Failed to print labels: ${error.message || 'Unknown error'}` });
        } finally {
            setIsPrinting(false);
        }
    }, [addToast, counts, inventoryKnown, priceListId, priceSelection, printMode, printableSkus, selected, skuType, zebraSelection]);

    const locationName = zebraSelection.locationOptions?.find((option) => option.value === locationGid)?.label ?? '';

    return (
        <BlockStack gap="400">
            <StyleSearchField
                locationOptions={zebraSelection.locationOptions}
                locationGid={locationGid}
                onLocationChange={zebraSelection.setSelectedLocationGid}
                searchType={searchType}
                onSearchTypeChange={handleSearchTypeChange}
                term={term}
                onTermChange={handleTermChange}
                isLoading={suggest.isLoading || resolve.isPending}
                matches={suggest.matches}
                hasMore={suggest.hasMore}
                error={suggest.error}
                resolveError={resolveError}
                showResults={!selected && suggest.debouncedTerm.length >= MIN_SEARCH_LENGTH}
                onSelect={handleSelect}
            />

            {selected?.masterProduct && <MasterProductCard masterProduct={selected.masterProduct} matchedBy={matchedBy} />}

            {selected?.variants && (
                <>
                    <PrintSettingsCard
                        printMode={printMode}
                        onPrintModeChange={onPrintModeChange}
                        skuType={skuType}
                        onSkuTypeChange={onSkuTypeChange}
                        catalogs={catalogs}
                        priceSelection={priceSelection}
                        onPriceSelectionChange={onPriceSelectionChange}
                        zebraSelection={zebraSelection}
                        labelPrintersQuery={labelPrintersQuery}
                        enableZebraCloudPrinting={enableZebraCloudPrinting}
                        showStoreSelect={false}
                    />

                    <StyleVariantTable
                        variants={selected.variants}
                        counts={counts}
                        onCountChange={handleCountChange}
                        skuType={skuType}
                        priceSelection={priceSelection}
                        inventoryKnown={inventoryKnown}
                        locationName={locationName}
                        onPrint={handlePrint}
                        isPrinting={isPrinting}
                    />
                </>
            )}
        </BlockStack>
    );
}
